import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button,
    DatePicker,
    FormCheckbox,
    Input,
    Select,
    Switch,
} from "@jhool-io/fe-components";
import { Controller, useForm } from "react-hook-form";
import { FormatOptionLabelMeta } from "react-select";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import {
    CPT_CODES,
    NoteLabels,
    NoteTypes,
    SessionServiceType,
    ModifierTypes,
    BILLABLE_PSYCHIATRY_NOTE_TYPES,
    BILLABLE_THERAPY_NOTE_TYPES,
} from "../../../notes/types/notes.types";
import {
    cn,
    displayNameInRightFormat,
    handleFormatDatePickerValue,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { SessionPlaceOfServiceCode } from "../../../../utils/types/session";
import { useFetchDiagnosisCodes } from "../../../../hooks/queries";
import {
    CPT_CODES_DESCRIPTION,
    CPT_CODES_PSYCH,
} from "../../../../utils/constants";
import FolderOpenIcon from "../../../../components/Icons/FolderOpen";
import { useCreateManualClaim } from "../../hooks/claims.mutations";
import useToast from "../../../../hooks/useToast";
import {
    AdditionalClaimInfo,
    ICreateManualClaim,
} from "../../types/claims.types";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { useDebounce } from "../../../../hooks/helpers";
import TimesIcon from "../../../../icons/Times";

type Options = {
    label: string;
    value: string;
};

interface Option {
    label: string;
    value: string;
}

interface CreateClaimProps {
    onFormSubmit: () => void;
}

const isPsychNoteValue = (noteType?: string) =>
    Boolean(
        noteType &&
            BILLABLE_PSYCHIATRY_NOTE_TYPES.includes(noteType as NoteTypes)
    );

const schema = yup.object().shape({
    pos_code: yup.string().required("POS code is required"),
    note_type: yup.string().required("Note type is required"),
    cpt_code: yup.string().when("note_type", {
        is: (value: string) => !isPsychNoteValue(value),
        then: (s) => s.required("CPT code is required"),
        otherwise: (s) => s.nullable(),
    }),
    additional_cpt_codes: yup.array().of(yup.string()).nullable(),
    duration: yup.string().when("note_type", {
        is: (value: string) => isPsychNoteValue(value),
        then: (s) =>
            s
                .required("Duration is required")
                .matches(/^[1-9]\d*$/, "Duration must be a positive number"),
        otherwise: (s) => s.nullable(),
    }),
    minutes_per_unit: yup.string().when("note_type", {
        is: (value: string) => isPsychNoteValue(value),
        then: (s) =>
            s
                .required("Minutes per unit is required")
                .matches(
                    /^[1-9]\d*$/,
                    "Minutes per unit must be a positive integer"
                ),
        otherwise: (s) => s.nullable(),
    }),
    unit_assignments: yup.array().when("note_type", {
        is: (value: string) => isPsychNoteValue(value),
        then: (s) =>
            s
                .of(
                    yup.object().shape({
                        unit: yup.string().required("Unit is required"),
                        cpt_code: yup.string().required("CPT code is required"),
                    })
                )
                .min(1, "At least one unit assignment is required"),
        otherwise: (s) => s.nullable(),
    }),
    primary_diagnosis: yup
        .string()
        .required("Primary diagnosis code is required"),
    secondary_diagnosis: yup.string().nullable(),
    tertiary_diagnosis: yup.string().nullable(),
    additional_one: yup.string().nullable(),
    additional_two: yup.string().nullable(),
    modifier_codes: yup.array().nullable(),
    provider_id: yup.string().required("Provider is required"),
    client_id: yup.string().required("Client is required"),
    date_of_service: yup.date().required("Date of service is required"),
    appointment_type: yup.string().required("Session type is required"),
    session_start_time: yup.string().required("Session start time is required"),
    session_end_time: yup
        .string()
        .required("Session end time is required")
        .test({
            name: "is-greater",
            message: "Session end time should be later than the start time",
            test: (
                value: string | undefined,
                context: {
                    parent: {
                        session_start_time: string;
                    };
                }
            ) => {
                const { session_start_time: sessionStartTime } = context.parent;
                if (!sessionStartTime || !value) {
                    return true;
                }
                const start = new Date(`1970-01-01T${sessionStartTime}:00`);
                const end = new Date(`1970-01-01T${value}:00`);
                return end > start;
            },
        }),
});

export default function CreateManualClaimForm({
    onFormSubmit,
}: CreateClaimProps) {
    const [diagnosticSearchValue, setDiagnosticSearchValue] =
        React.useState("");
    const [activeDiagnosisField, setActiveDiagnosisField] = React.useState<
        string | null
    >(null);
    const [dateOfService, setDateOfService] = React.useState<Date | null>(null);
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );
    const [isSelfPay, setIsSelfPay] = React.useState(false);
    const [providerSearchValue, setProviderSearchValue] = React.useState("");
    const [clientSearchValue, setClientSearchValue] = React.useState("");

    const debouncedProviderSearchValue = useDebounce(providerSearchValue, 200);
    const debouncedClientSearchValue = useDebounce(clientSearchValue, 200);
    const diagnosisSearch = useDebounce(diagnosticSearchValue.trim(), 500);

    const isSmallScreen = window.matchMedia("(max-width: 500px)").matches;

    const providers = useFetchProviders(
        { search_string: debouncedProviderSearchValue },
        providerSearchValue.length > 0
    );

    const clients = useFetchClientList(
        { search_string: debouncedClientSearchValue },
        clientSearchValue.length > 0
    );

    const {
        data: diagnosisCodes,
        isLoading,
        isFetching,
        error,
    } = useFetchDiagnosisCodes(false, diagnosisSearch);

    const createManualClaim = useCreateManualClaim();

    const { toast } = useToast();

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
        register,
        reset,
        getValues,
    } = useForm<ICreateManualClaim>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            unit_assignments: [],
            additional_cpt_codes: [],
        },
    });

    const [noteCategory, setNoteCategory] = React.useState<"therapy" | "psych">(
        "therapy"
    );
    const isPsychNoteType = noteCategory === "psych";

    const handleChangeNoteCategory = (category: "therapy" | "psych") => {
        if (category === noteCategory) return;
        setNoteCategory(category);
        reset({
            ...getValues(),
            note_type: "" as NoteTypes,
            cpt_code: "",
            additional_cpt_codes: [],
            unit_assignments: [],
            duration: "",
            minutes_per_unit: "",
        });
    };

    const noteCategoryOptions = [
        {
            title: "Therapy",
            key: "therapy",
            onClick: () => handleChangeNoteCategory("therapy"),
        },
        {
            title: "Psych",
            key: "psych",
            onClick: () => handleChangeNoteCategory("psych"),
        },
    ];

    const durationInMinutes = watch("duration") ? Number(watch("duration")) : 0;
    const minutesPerUnit = Number(watch("minutes_per_unit"));

    const debouncedDuration = useDebounce(durationInMinutes);
    const debouncedMinutesPerUnit = useDebounce(minutesPerUnit);

    const getTotalUnitsAmount =
        debouncedMinutesPerUnit > 0
            ? debouncedDuration / debouncedMinutesPerUnit
            : 0;

    const finalUnitsAmount =
        getTotalUnitsAmount >= 0.5
            ? Math.round(getTotalUnitsAmount)
            : Math.floor(getTotalUnitsAmount);

    const watchedUnitAssignments = watch("unit_assignments");
    const currentUnitAssignments = React.useMemo(
        () => watchedUnitAssignments || [],
        [watchedUnitAssignments]
    );

    React.useEffect(() => {
        if (!isPsychNoteType || finalUnitsAmount <= 0) {
            return;
        }

        const currentLength = currentUnitAssignments.length;
        if (finalUnitsAmount === currentLength) {
            return;
        }

        if (finalUnitsAmount > currentLength) {
            const repeatCptCode =
                currentUnitAssignments[currentUnitAssignments.length - 1]
                    ?.cpt_code || "";
            const newAssignments = [...currentUnitAssignments];
            for (let i = currentLength; i < finalUnitsAmount; i += 1) {
                newAssignments.push({
                    unit: String(i + 1),
                    cpt_code: repeatCptCode,
                });
            }
            setValue("unit_assignments", newAssignments);
        } else {
            setValue(
                "unit_assignments",
                currentUnitAssignments.slice(0, finalUnitsAmount)
            );
        }
    }, [finalUnitsAmount, isPsychNoteType, currentUnitAssignments, setValue]);

    const navigate = useNavigate();
    const PlaceOfServiceOptions = [
        ...Object.values(SessionPlaceOfServiceCode).map((place) => ({
            value: place,
            label: place,
        })),
    ];

    const providerForSelectOptions = providers?.data?.data?.map((provider) => ({
        label: displayNameInRightFormat(provider.provider_name),
        value: provider.provider_id,
    }));

    const clientsForSelectOptions = clients?.data?.data?.map((client) => ({
        label: displayNameInRightFormat({
            firstName: client.first_name,
            lastName: client.last_name,
        }),
        value: client.client_id,
    }));

    const getSessionTypeSelectOptions = Object.values(SessionServiceType).map(
        (sessionType) => ({
            label: (
                <span style={{ textTransform: "capitalize" }}>
                    {removeEnumUnderscore(sessionType)}
                </span>
            ),
            value: sessionType,
        })
    );

    const noteTypesForCategory =
        noteCategory === "psych"
            ? BILLABLE_PSYCHIATRY_NOTE_TYPES
            : BILLABLE_THERAPY_NOTE_TYPES;

    const getNoteTypeSelectOptions = noteTypesForCategory.map((noteType) => ({
        label: NoteLabels[noteType],
        value: noteType,
    }));

    const DiagnosisCodesForSelect = diagnosisCodes?.data.map((diagnosis) => ({
        label: `${diagnosis.code} ${diagnosis.description}`,
        value: diagnosis.diagnosis_code_id,
    }));

    // Get filter options for diagnosis list
    const filterDiagnosisOptions = (phrases: string[]) => {
        const filteredOptions = DiagnosisCodesForSelect?.filter((option) =>
            phrases.every(
                (phrase) =>
                    option &&
                    option.label.toLowerCase().includes(phrase.toLowerCase())
            )
        );
        return filteredOptions;
    };

    const modifiersTypesForSelect: Options[] = [
        ...Object.values(ModifierTypes).map((item) => ({
            value: item.split(" ")[0],
            label: item,
        })),
    ];

    const additionalClaimInfoForSelect: Options[] = Object.values(
        AdditionalClaimInfo
    ).map((info) => ({
        value: info,
        label: info,
    }));

    const CPTOptions = CPT_CODES.sort((a, b) => Number(a) - Number(b)).map(
        (code) => ({
            label: `${removeEnumUnderscore(code)} - ${
                CPT_CODES_DESCRIPTION[code]
            }`,
            value: code,
        })
    );

    const PsychCPTOptions = CPT_CODES_PSYCH.map((code) => ({
        label: `${removeEnumUnderscore(code)} - ${CPT_CODES_DESCRIPTION[code]}`,
        value: code,
    }));

    // Function to call when file(s) are dropped in dropzone
    const onDrop = React.useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else setSelectedFiles([...acceptedFiles]);
        },
        [selectedFiles]
    );

    // Initialize the useDropzone hook
    const { getRootProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: {
            "application/pdf": [".pdf"],
            "image/*": [".png", ".webp", ".jpeg", ".jpg", ".avif"],
        },
    });

    const formData = new FormData();

    // Function for setting selected files
    const handleSetSelectedFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...e.target.files, ...selectedFiles]);
            } else setSelectedFiles([...e.target.files]);
        }
    };

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const convertZeroTimeFromDoubleToSingle = (timeString: string) => {
        return timeString === "00" ? "0" : timeString;
    };

    const onSubmit = (payload: ICreateManualClaim) => {
        let diagnosisToSend: { id: string; label: string }[] = [
            { id: payload.primary_diagnosis, label: "primary" },
        ];

        if (payload.secondary_diagnosis) {
            diagnosisToSend = [
                ...diagnosisToSend,
                {
                    id: payload.secondary_diagnosis,
                    label: "secondary",
                },
            ];
        }
        if (payload.tertiary_diagnosis) {
            diagnosisToSend = [
                ...diagnosisToSend,
                {
                    id: payload.tertiary_diagnosis,
                    label: "tertiary",
                },
            ];
        }
        if (payload.additional_one) {
            diagnosisToSend = [
                ...diagnosisToSend,
                {
                    id: payload.additional_one,
                    label: "additional_one",
                },
            ];
        }

        if (payload.additional_two) {
            diagnosisToSend = [
                ...diagnosisToSend,
                {
                    id: payload.additional_two,
                    label: "additional_two",
                },
            ];
        }

        const [sessionStartHr, sesssionStartMin] =
            payload.session_start_time.split(":");
        const [sessionEndHr, sesssionEndMin] =
            payload.session_end_time.split(":");

        formData.append("provider_id", payload.provider_id);
        formData.append("client_id", payload.client_id);
        formData.append(
            "session_start_hr",
            convertZeroTimeFromDoubleToSingle(sessionStartHr)
        );
        formData.append(
            "session_start_min",
            convertZeroTimeFromDoubleToSingle(sesssionStartMin)
        );
        formData.append(
            "session_end_hr",
            convertZeroTimeFromDoubleToSingle(sessionEndHr)
        );
        formData.append(
            "session_end_min",
            convertZeroTimeFromDoubleToSingle(sesssionEndMin)
        );
        formData.append("note_type", payload.note_type);
        formData.append("appointment_type", String(payload.appointment_type));
        formData.append("pos_code", payload.pos_code);
        formData.append(
            "date_of_service",
            handleFormatDatePickerValue(payload.date_of_service)
        );
        if (isSelfPay) {
            formData.append("insurance_provider_id", "self_pay");
        }

        if (isPsychNoteType) {
            const assignments = payload.unit_assignments || [];
            const primaryCptCode = assignments[0]?.cpt_code || "";
            formData.append("cpt_code", primaryCptCode);

            const uniqueAdditional = Array.from(
                new Set(
                    assignments
                        .map((a) => a.cpt_code)
                        .filter((code, index) => code && index > 0)
                )
            ).filter((code) => code !== primaryCptCode);
            formData.append(
                "additional_cpt_codes",
                JSON.stringify(uniqueAdditional)
            );

            formData.append(
                "unit_calculation",
                JSON.stringify({
                    duration: Number(payload.duration) || 0,
                    total_units: finalUnitsAmount || 0,
                    minutes_per_unit: Number(payload.minutes_per_unit) || 0,
                    unit_assignments: assignments.map((a) => ({
                        unit: Number(a.unit),
                        cpt_code: a.cpt_code,
                    })),
                })
            );
        } else {
            formData.append("cpt_code", payload.cpt_code);
            const additional = (payload.additional_cpt_codes || []).filter(
                (code) => code && code !== payload.cpt_code
            );
            formData.append("additional_cpt_codes", JSON.stringify(additional));
        }

        if (diagnosisToSend.length > 0) {
            for (let i = 0; i < diagnosisToSend.length; i += 1) {
                formData.append(
                    "diagnosis_codes",
                    JSON.stringify(diagnosisToSend)
                );
            }
        }
        if (payload.modifier_codes && payload.modifier_codes.length > 0) {
            for (let i = 0; i < [...payload.modifier_codes].length; i += 1) {
                formData.append(
                    "modifier_codes",
                    JSON.stringify(payload.modifier_codes)
                );
            }
        }

        if (payload.additional_claim_info) {
            formData.append(
                "additional_claim_info",
                payload.additional_claim_info
            );
        }

        if (selectedFiles && selectedFiles.length > 0) {
            for (let i = 0; i < [...selectedFiles].length; i += 1) {
                formData.append("note_file", [...selectedFiles][i]);
            }
        }

        createManualClaim.mutate(formData, {
            onSuccess: (res) => {
                toast({
                    mode: "success",
                    message:
                        res.message || "Manual claim created successfully!",
                });
                onFormSubmit();
                if (isSelfPay) {
                    navigate(
                        `/billing-and-claims/${payload.client_id}/${res.data.note_id}`
                    );
                } else {
                    navigate(
                        `/claims/${res.claim_id}/${res.data.claim.client_id}/${res.data.claim.note_id}`
                    );
                }
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not create claim at this time.",
                });
            },
        });
    };

    return (
        <form id="create-claim" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <FormCheckbox
                    label="Create as self-pay"
                    checked={isSelfPay}
                    onChange={() => setIsSelfPay(!isSelfPay)}
                />
            </div>
            <div className="fg">
                <Controller
                    name="provider_id"
                    control={control}
                    render={({ field }) => (
                        <Select
                            isSearchable
                            filterOption={() => true}
                            placeholder="Provider name"
                            aria-label="Provider name"
                            label="Provider name"
                            options={providerForSelectOptions || []}
                            onChange={(val) => {
                                field.onChange((val as Options).value);
                                setProviderSearchValue("");
                            }}
                            onInputChange={(value) => {
                                setProviderSearchValue(value);
                            }}
                            openMenuOnClick={Boolean(field.value)}
                            menuIsOpen={debouncedProviderSearchValue.length > 0}
                            noOptionsMessage={() =>
                                providers.isFetching
                                    ? "Fetching providers..."
                                    : "No options"
                            }
                            classNames={{
                                input: () => "text-sm",
                            }}
                            hasError={!!errors.provider_id}
                            errorText={errors.provider_id?.message}
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="client_id"
                    control={control}
                    render={({ field }) => (
                        <Select
                            isSearchable
                            filterOption={() => true}
                            placeholder="Client name"
                            aria-label="Client name"
                            label="Client name"
                            options={clientsForSelectOptions || []}
                            onChange={(val) => {
                                field.onChange((val as Options).value);
                                setClientSearchValue("");
                            }}
                            onInputChange={(value) => {
                                setClientSearchValue(value);
                            }}
                            openMenuOnClick={Boolean(field.value)}
                            menuIsOpen={debouncedClientSearchValue.length > 0}
                            noOptionsMessage={() => {
                                return clients.isFetching
                                    ? "Fetching clients..."
                                    : "No options";
                            }}
                            classNames={{
                                input: () => "text-sm",
                            }}
                            hasError={!!errors.client_id}
                            errorText={errors.client_id?.message}
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="date_of_service"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="Date of Service"
                            selected={dateOfService}
                            onChange={(date) => {
                                field.onChange(date);
                                setDateOfService(date);
                            }}
                            hasError={!!errors.date_of_service}
                            errorText={
                                errors.date_of_service?.type === "typeError"
                                    ? "invalid date value"
                                    : errors.date_of_service?.message
                            }
                            maxDate={new Date(Date.now())}
                        />
                    )}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    {...register("session_start_time")}
                    label="Start time"
                    placeholder="Start time"
                    hasError={!!errors.session_start_time}
                    errorText={errors.session_start_time?.message}
                    type="time"
                />

                <Input
                    {...register("session_end_time")}
                    label="End time"
                    placeholder="End time"
                    hasError={!!errors.session_end_time}
                    errorText={errors.session_end_time?.message}
                    type="time"
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="appointment_type"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Session type"
                            placeholder="Select session type"
                            isSearchable
                            options={getSessionTypeSelectOptions}
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Options).value);
                            }}
                            value={getSessionTypeSelectOptions.find(
                                (option) =>
                                    option.value === watch("appointment_type")
                            )}
                            hasError={!!errors.appointment_type}
                            errorText={errors.appointment_type?.message}
                        />
                    )}
                />
                <Controller
                    name="pos_code"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="POS code"
                            placeholder="Select POS code"
                            options={PlaceOfServiceOptions}
                            isSearchable
                            isLongListInDialog
                            onChange={(val) =>
                                field.onChange((val as Options).value)
                            }
                            hasError={!!errors.pos_code}
                            errorText={errors.pos_code?.message}
                            formatOptionLabel={(
                                selectData: unknown,
                                formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                            ) => {
                                return formatOptionLabelMeta.context === "menu"
                                    ? (selectData as Options).label
                                    : truncateString(
                                          (selectData as Options)
                                              .label as string,
                                          35
                                      );
                            }}
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Switch
                    switchOptions={noteCategoryOptions}
                    activeSwitch={noteCategory}
                />
            </div>
            <div className="fg">
                <Controller
                    name="note_type"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Note type"
                            placeholder="Select note type"
                            isSearchable
                            options={getNoteTypeSelectOptions}
                            isLongListInDialog
                            value={
                                getNoteTypeSelectOptions.find(
                                    (opt) => opt.value === field.value
                                ) || null
                            }
                            onChange={(val) => {
                                field.onChange((val as Options).value);
                            }}
                            hasError={!!errors.note_type}
                            errorText={errors.note_type?.message}
                        />
                    )}
                />
            </div>
            {isPsychNoteType ? (
                <>
                    <div className="fg fg-space-between three flex">
                        <Input
                            {...register("duration")}
                            label="Duration (in minutes)"
                            placeholder="Duration"
                            hasError={!!errors.duration}
                            errorText={errors.duration?.message}
                        />
                        <Input
                            {...register("minutes_per_unit")}
                            label="Mins per unit"
                            placeholder="Mins per unit"
                            hasError={!!errors.minutes_per_unit}
                            errorText={errors.minutes_per_unit?.message}
                        />
                        <Input
                            label="Total units"
                            placeholder="Total units"
                            value={finalUnitsAmount}
                            disabled
                        />
                    </div>
                    {currentUnitAssignments.map((assignment, i) => (
                        <div
                            key={`unit-${i + 1}`}
                            className="fg flex justify-between gap-[--spacing-items]"
                        >
                            <div className="basis-[80px]">
                                <Input
                                    {...register(`unit_assignments.${i}.unit`)}
                                    label="Unit"
                                    placeholder="Unit"
                                    defaultValue={assignment.unit}
                                    disabled
                                />
                            </div>
                            <div className="flex-1">
                                <Controller
                                    name={`unit_assignments.${i}.cpt_code`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="CPT Code"
                                            placeholder="CPT Code"
                                            options={PsychCPTOptions}
                                            value={PsychCPTOptions.find(
                                                (code) =>
                                                    code.value === field.value
                                            )}
                                            onChange={(val) =>
                                                field.onChange(
                                                    (val as Options).value
                                                )
                                            }
                                            formatOptionLabel={(
                                                selectData,
                                                meta
                                            ) => {
                                                return meta.context === "menu"
                                                    ? (selectData as Options)
                                                          .label
                                                    : truncateString(
                                                          (
                                                              selectData as Options
                                                          ).label,
                                                          isSmallScreen
                                                              ? 20
                                                              : 60
                                                      );
                                            }}
                                            isSearchable
                                            isLongListInDialog
                                            hasError={
                                                !!errors.unit_assignments?.[i]
                                                    ?.cpt_code
                                            }
                                            errorText={
                                                errors.unit_assignments?.[i]
                                                    ?.cpt_code?.message
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    ))}
                </>
            ) : (
                <>
                    <div className="fg">
                        <Controller
                            name="cpt_code"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label="CPT Code"
                                    placeholder="CPT Code"
                                    options={CPTOptions}
                                    onChange={(val) =>
                                        field.onChange((val as Options).value)
                                    }
                                    hasError={!!errors.cpt_code}
                                    errorText={errors.cpt_code?.message}
                                    isSearchable
                                    isLongListInDialog
                                    formatOptionLabel={(
                                        selectData: unknown,
                                        formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                                    ) => {
                                        return formatOptionLabelMeta.context ===
                                            "menu"
                                            ? (selectData as Options).label
                                            : truncateString(
                                                  (selectData as Options)
                                                      .label as string,
                                                  35
                                              );
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="additional_cpt_codes"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label="Additional CPT codes"
                                    placeholder="Additional CPT codes"
                                    options={CPTOptions}
                                    isMulti
                                    isSearchable
                                    isLongListInDialog
                                    value={CPTOptions.filter((option) =>
                                        (field.value || []).includes(
                                            option.value
                                        )
                                    )}
                                    onChange={(val) => {
                                        field.onChange(
                                            (
                                                val as {
                                                    label: string;
                                                    value: string;
                                                }[]
                                            ).map((code) => code.value)
                                        );
                                    }}
                                    multiHasValues={
                                        (watch("additional_cpt_codes") || [])
                                            .length > 0
                                    }
                                />
                            )}
                        />
                    </div>
                </>
            )}
            <div className="fg">
                <Controller
                    name="primary_diagnosis"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Primary diagnosis"
                            placeholder="Primary diagnosis"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          diagnosticSearchValue.split(" ")
                                      )
                            }
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 40
                                      );
                            }}
                            isSearchable
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            hasError={!!errors.primary_diagnosis}
                            errorText={errors.primary_diagnosis?.message}
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setDiagnosticSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setDiagnosticSearchValue("");
                                }
                            }}
                            filterOption={
                                diagnosisSearch.length >= 2
                                    ? () => true
                                    : undefined
                            }
                            closeMenuOnSelect
                        />
                    )}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="secondary_diagnosis"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Secondary diagnosis"
                            placeholder="Secondary diagnosis"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          diagnosticSearchValue.split(" ")
                                      )
                            }
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            isSearchable
                            isLongListInDialog
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Option).value);
                            }}
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 25
                                      );
                            }}
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setDiagnosticSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setDiagnosticSearchValue("");
                                }
                            }}
                            filterOption={
                                diagnosisSearch.length >= 2
                                    ? () => true
                                    : undefined
                            }
                            hasError={!!errors.secondary_diagnosis}
                            errorText={errors.secondary_diagnosis?.message}
                            isClearable
                            closeMenuOnSelect
                        />
                    )}
                />

                <Controller
                    name="tertiary_diagnosis"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Tertiary diagnosis"
                            placeholder="Tertiary diagnosis"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          diagnosticSearchValue.split(" ")
                                      )
                            }
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            isSearchable
                            isLongListInDialog
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Option).value);
                            }}
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 25
                                      );
                            }}
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setDiagnosticSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setDiagnosticSearchValue("");
                                }
                            }}
                            filterOption={
                                diagnosisSearch.length >= 2
                                    ? () => true
                                    : undefined
                            }
                            hasError={!!errors.tertiary_diagnosis}
                            errorText={errors.tertiary_diagnosis?.message}
                            isClearable
                            closeMenuOnSelect
                        />
                    )}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="additional_one"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Additional Diagnosis 1"
                            placeholder="Additional Diagnosis 1"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          diagnosticSearchValue.split(" ")
                                      )
                            }
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            isSearchable
                            isLongListInDialog
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Option).value);
                            }}
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 25
                                      );
                            }}
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setDiagnosticSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setDiagnosticSearchValue("");
                                }
                            }}
                            filterOption={
                                diagnosisSearch.length >= 2
                                    ? () => true
                                    : undefined
                            }
                            hasError={!!errors.additional_one}
                            errorText={errors.additional_one?.message}
                            isClearable
                            closeMenuOnSelect
                        />
                    )}
                />

                <Controller
                    name="additional_two"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Additional diagnosis two"
                            placeholder="Additional diagnosis two"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          diagnosticSearchValue.split(" ")
                                      )
                            }
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            isSearchable
                            isLongListInDialog
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Option).value);
                            }}
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 25
                                      );
                            }}
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setDiagnosticSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setDiagnosticSearchValue("");
                                }
                            }}
                            filterOption={
                                diagnosisSearch.length >= 2
                                    ? () => true
                                    : undefined
                            }
                            hasError={!!errors.additional_two}
                            errorText={errors.additional_two?.message}
                            isClearable
                            closeMenuOnSelect
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="modifier_codes"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Modifier code"
                            placeholder="Modifier code"
                            options={modifiersTypesForSelect}
                            isSearchable
                            isLongListInDialog
                            isMulti
                            onChange={(val) => {
                                field.onChange(
                                    (
                                        val as {
                                            label: string;
                                            value: string;
                                        }[]
                                    ).map((code) => code.value)
                                );
                            }}
                            hasError={!!errors.modifier_codes}
                            errorText={
                                errors?.modifier_codes?.type === "min"
                                    ? "Modifier codes are required"
                                    : errors?.diagnosis_codes?.message
                            }
                            multiHasValues={Boolean(watch("modifier_codes"))}
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="additional_claim_info"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Additional claim info (Box 19)"
                            placeholder="Additional claim info (Box 19)"
                            options={additionalClaimInfoForSelect}
                            isLongListInDialog
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Options).value);
                            }}
                            isClearable
                        />
                    )}
                />
            </div>
            <div
                className={cn(
                    "flex w-full items-center gap-x-[--spacing-items]  border border-dashed border-[--color-text-link] rounded-lg px-[--padding-card-x] py-[--padding-card-y]",
                    {
                        "border-[2px] border-dashed border-stroke":
                            isDragActive,
                    }
                )}
                {...getRootProps()}
            >
                <div className="p-16 bg-[rgba(204,250,233,0.50)] rounded-md shrink-0">
                    <FolderOpenIcon />
                </div>
                <div className="flex flex-col">
                    <div className="text-base font-medium">
                        <label
                            htmlFor="file-upload"
                            className="font-semibold text-textLink"
                        >
                            Click to upload session note{" "}
                            <input
                                className="w-full absolute left-[-99999px]"
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                onChange={handleSetSelectedFiles}
                                disabled={selectedFiles?.length === 1}
                                max="1"
                            />
                        </label>
                        or drag your file here
                    </div>
                    <p className="text-caption font-normal">
                        Maximum file size of 10 MB: .png, .jpg, .jpeg
                    </p>
                </div>
            </div>

            {createManualClaim.uploadProgress > 0 && (
                <div className="mt-[--spacing-items] h-2 bg-stroke rounded-md">
                    <div
                        role="progressbar"
                        aria-label="file upload progress value"
                        aria-valuenow={createManualClaim.uploadProgress}
                        style={{
                            width: `${createManualClaim.uploadProgress}%`,
                        }}
                        className="h-full rounded-r8 bg-primary rounded-md"
                    />
                </div>
            )}

            <div className="mt-[--spacing-form-items]">
                {selectedFiles &&
                    selectedFiles.map((file, i) => (
                        <div
                            key={`${file.name} - ${i + 1}`}
                            className="flex items-center justify-between w-full [border:1px_solid_var(--color-stroke)] rounded-md px-[--padding-card-x] py-[--padding-card-y] my-[--spacing-items]"
                        >
                            <div className="text-caption text-xs text-textSec">
                                {file.name}
                            </div>
                            <Button
                                variant="can-hover"
                                aria-label="delete icon"
                                size="auto"
                                onClick={() =>
                                    handleRemoveSelectedFile(file.name)
                                }
                            >
                                <TimesIcon />
                            </Button>
                        </div>
                    ))}
            </div>
        </form>
    );
}
